import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthProvider'
import { api } from '../lib/api'
import { supabase } from '../lib/supabase'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Loader2, X, User, Mail, AlertCircle, CheckCircle, Trash2 } from 'lucide-react'

export default function ProfileModal({ onClose }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const isGoogleUser =
    user?.app_metadata?.provider === 'google' ||
    user?.identities?.some((i) => i.provider === 'google')

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await api.getUser()
        setProfile(data)
        setFirstName(data.firstName || '')
        setLastName(data.lastName || '')
      } catch (err) {
        setError('Failed to load profile')
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const first = firstName.trim()
      const last = lastName.trim()
      await api.updateUser(user.id, {
        firstName: first || null,
        lastName: last || null,
        name: [first, last].filter(Boolean).join(' ') || null,
      })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)
    try {
      await api.deleteAccount(user.id)
      await supabase.auth.signOut()
      navigate('/')
    } catch (err) {
      setError(err.message || 'Failed to delete account')
      setDeleting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-gold/40 rounded flex items-center justify-center bg-gold/10">
              <User className="w-5 h-5 text-gold" />
            </div>
            <h2 className="text-xl font-semibold font-display text-charcoal">Edit Profile</h2>
          </div>
          <button
            onClick={onClose}
            className="text-charcoal-muted hover:text-charcoal transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-gold animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              {success && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <p className="text-emerald-600 text-sm">Profile updated successfully</p>
                </div>
              )}

              {/* Email — always read-only */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-muted" />
                  <Input
                    value={user?.email || ''}
                    readOnly
                    className="pl-9 bg-surface-linen border-surface-border text-charcoal-muted cursor-default select-none"
                  />
                </div>
                <p className="text-xs text-charcoal-muted mt-1">
                  {isGoogleUser
                    ? 'Your email is managed by Google and cannot be changed here.'
                    : 'Email cannot be changed. Contact support if needed.'}
                </p>
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">
                  First name
                </label>
                <Input
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setError(null) }}
                  placeholder="Enter your first name"
                  maxLength={100}
                  className="bg-surface-linen border-surface-border text-charcoal"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">
                  Last name
                </label>
                <Input
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); setError(null) }}
                  placeholder="Enter your last name"
                  maxLength={100}
                  className="bg-surface-linen border-surface-border text-charcoal"
                />
              </div>

              {isGoogleUser && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-blue-700 text-xs">
                    Your account is linked to Google. You can update your name above,
                    but email and password are managed through your Google account.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gold text-black font-semibold hover:bg-gold/90"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Danger Zone */}
          {!loading && (
            <div className="mt-6 pt-6 border-t border-surface-border">
              <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="w-full text-left px-4 py-3 rounded border border-red-200 bg-red-50 text-red-600 text-sm hover:bg-red-100 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4 shrink-0" />
                  Delete Account
                </button>
              ) : (
                <div className="p-4 rounded border border-red-300 bg-red-50 space-y-3">
                  <p className="text-red-700 text-sm font-medium">Are you absolutely sure?</p>
                  <p className="text-red-600 text-xs leading-relaxed">
                    This will permanently delete your account and all associated data including
                    donation history. This action cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 px-3 py-2 text-sm rounded border border-surface-border text-charcoal hover:bg-surface-linen transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 px-3 py-2 text-sm rounded border border-red-400 bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {deleting ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" />Deleting...</>
                      ) : (
                        <><Trash2 className="w-3.5 h-3.5" />Yes, Delete</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
